import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { filter, Subscription, take } from 'rxjs';

import { TokensService } from 'app/shared/services/tokens.service';
import { AuthService } from 'app/auth/auth.service';

import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { ToastComponent } from '../toast/toast.component';

import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import { IToastConfig } from '../../interfaces/toast-config.interfaces';
import { IPaymentResult } from '../../../interfaces/payments.interfaces';

import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { ETokenType, ETokenUsageCase } from 'app/shared/enums/tokens.enums';
import { EToastType } from '../../enums/toast.enums';
import { EPaymentStatus } from '../../../enums/payments.enums';

@Component({
  selector: 'stxt-token-status',
  templateUrl: './token-status.component.html',
  styleUrls: ['./token-status.component.scss'],
  standalone: true,
  imports: [CommonModule, SvgIconComponent, ToastComponent]
})
export class TokenStatusComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type?: ETokenType;
  @Input() tokenPlans?: number;
  @Input() usageCase?: ETokenUsageCase;
  @Input() tokensBalance: number;
  @Output() tokenType: EventEmitter<ETokenType> = new EventEmitter<ETokenType>();
  isToastMessage: boolean = false;
  ETokenUsageCase = ETokenUsageCase;
  ETokenType = ETokenType;
  previousStatus: ETokenType;
  arrow_down_icon: ISvgConfig = { name: 'arrow_down', fill: ESvgTypes.None };
  toast: IToastConfig = {
    toastType: EToastType.FAIL
  };
  subscriptions = new Subscription();
  private currentStatus: ETokenType;

  constructor(
    private readonly tokensService: TokensService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeTokens();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tokensBalance']) {
      this.calculateStatus();
    }
  }

  private initializeTokens(): void {
    if (this.tokensBalance !== undefined) {
      this.calculateStatus();
    } else {
      this.subscriptions.add(
        this.tokensService.tokensBalance$.subscribe(balance => {
          this.tokensBalance = balance;
          this.calculateStatus();
        })
      );

      this.subscriptions.add(
        this.authService.$isUser.pipe(filter(Boolean), take(1)).subscribe(() => {
          this.tokensService.fetchTokensBalance();
        })
      );
    }
  }

  toggleToast(paymentResult: IPaymentResult): void {
    switch (paymentResult.status) {
      case EPaymentStatus.Success:
        this.toast = {
          toastHeading: 'Wallet reloaded',
          toastSubheading: `You have ${this.tokensBalance} tokens`,
          toastType: EToastType.SUCCESS
        };
        break;
      case EPaymentStatus.Error:
        this.toast = {
          toastHeading: 'Wallet could not be reloaded',
          toastType: EToastType.FAIL
        };
        break;
    }
    this.isToastMessage = true;
  }

  private calculateStatus(): void {
    let status: ETokenType;

    if (this.usageCase === ETokenUsageCase.Card && this.tokensBalance === 0) {
      status = ETokenType.Empty;
    } else if (this.tokensBalance === 0 && this.usageCase === ETokenUsageCase.Header) {
      status = ETokenType.Add;
      this.type = ETokenType.Add;
    } else if (this.tokensBalance < 10) {
      status = ETokenType.LowBalance;
      this.type = ETokenType.LowBalance;
    } else {
      status = ETokenType.HasFunds;
      this.type = ETokenType.HasFunds;
    }

    if (this.currentStatus === status) return;
    this.currentStatus = status;
    // To prevent ExpressionChanged error.
    setTimeout(() => {
      this.tokenType.emit(status);
    });
  }

  get statusType(): ETokenType {
    return this.currentStatus;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
